import type { ReactNode } from 'react'

import type {
  ComboboxClassNames,
  ComboboxProps,
} from '@fv/client-components/combobox'
import { ComboboxBase } from '@fv/client-components/combobox'

export type ShipperClassNames<T> = ComboboxClassNames<T> & {
  container?: string
}

export type ShipperInternalKeys =
  | 'portalToRoot'
  | 'classNames'
  | 'open'
  | 'onOpenChange'

export const defaultComboboxOptionClassName =
  'mx-2 mb-2 p-2 cursor-pointer last:mb-0 border'

export const blueOptionClassName = `${defaultComboboxOptionClassName} bg-fv-blue-50 border-fv-blue-200 hover:bg-fv-blue-100 hover:border-fv-blue data-[highlighted]:bg-fv-blue-100 data-[highlighted]:border-fv-blue`

export const shipperDropdownClassNames = {
  positioner: 'border border-fv-gray bg-fv-beer-xlight z-[9999]',
  list: 'max-h-60 overflow-y-auto py-2 data-[empty]:hidden',
  option: `${blueOptionClassName} `,
  create: blueOptionClassName,
  empty: 'p-2 text-fv-gray-dark',
  loading: 'p-2 text-fv-gray-dark',
}

export const shipperInputClassNames = {
  root: 'flex items-center bg-white border border-[#b3b3b3] border-b-[0.115rem] border-b-fv-blue min-h-[2.8rem] px-2 focus-within:border-b-[#ff6501] hover:border-b-[#ff6501]',
  input:
    'w-full outline-hidden bg-transparent text-[#333] leading-[1.4rem] flex-1',
  clear: 'text-fv-blue cursor-pointer hover:text-[#333] px-1',
}

export const defaultGetOptionLabel = <T,>(o: T) =>
  (o as { label: string }).label ?? String(o)

export function boldSearchText(label: string, search: string): ReactNode {
  if (!search) return label
  const lowerLabel = label.toLowerCase()
  const lowerSearch = search.toLowerCase()
  const matchStart = lowerLabel.indexOf(lowerSearch)
  if (matchStart < 0) return label

  const matchEnd = matchStart + search.length
  return (
    <span>
      <span>{label.slice(0, matchStart)}</span>
      <strong>{label.slice(matchStart, matchEnd)}</strong>
      <span>{label.slice(matchEnd)}</span>
    </span>
  )
}

type Props<T> = ComboboxProps<T> & {
  showTrigger?: boolean
}

export function ShipperComboBox<T>({ showTrigger, ...props }: Props<T>) {
  return (
    <ComboboxBase<T>
      portalToRoot
      {...props}
      classNames={{
        ...shipperDropdownClassNames,
        ...(showTrigger && {
          trigger:
            'absolute right-0 top-0 bottom-0 flex items-center px-2 text-fv-gray-dark cursor-pointer hover:text-[#333]',
        }),
        ...props.classNames,
      }}
    />
  )
}
